const focusablesSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable]',
].join(',');

export const findFirstFocusableWithin = <T = HTMLInputElement>(
  el?: HTMLElement | null,
) => (el ? (el?.querySelector(focusablesSelector) as T) : undefined);

export const findFocusables = (parent: HTMLElement = document.body) =>
  Array.from(parent.querySelectorAll(focusablesSelector)).filter(
    (it) => it.getAttribute('tabindex') !== '-1',
  );

export const findNextFocusable = (
  element?: Element | null,
): HTMLElement | undefined => {
  const focusableElements = findFocusables();

  if (!element) {
    return focusableElements[0] as HTMLElement;
  }

  // Find the index of the current element
  const currentIndex = focusableElements.indexOf(element);

  // If the current element is not found or it's the last element, return the first focusable element
  if (currentIndex === -1 || currentIndex === focusableElements.length - 1) {
    return focusableElements[0] as HTMLElement;
  }

  // Return the next focusable element
  return focusableElements[currentIndex + 1] as HTMLElement;
};

export const findPrevFocusable = (
  element?: Element | null,
): HTMLElement | undefined => {
  const focusableElements = findFocusables();

  if (!element) {
    return focusableElements[focusableElements.length - 1] as HTMLElement;
  }

  const currentIndex = focusableElements.indexOf(element);

  if (currentIndex === -1) {
    return focusableElements[focusableElements.length - 1] as HTMLElement;
  }

  const prevIndex =
    (currentIndex - 1 + focusableElements.length) % focusableElements.length;

  return focusableElements[prevIndex] as HTMLElement;
};

export const getScrollableParent = (
  element: HTMLElement,
): HTMLElement | null => {
  if (!element) {
    return null;
  }

  // Start with the element's parent
  let parent = element.parentElement;

  // Loop through parents until we find a scrollable one or reach the document body
  while (parent && parent !== document.body) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }

  // If no scrollable parent found, return the document body or null
  return (document.scrollingElement as HTMLElement) || document.documentElement;
};

export const isElementVisible = (
  element: HTMLElement | null | undefined,
  parent?: HTMLElement | null,
): boolean => {
  if (!element) {
    return false;
  }

  const scrollableParent = parent || getScrollableParent(element);

  if (!scrollableParent) {
    return false;
  }

  const elementRect = element.getBoundingClientRect();
  const parentRect = scrollableParent.getBoundingClientRect();
  const parentBottomPadding = parseFloat(
    getComputedStyle(scrollableParent)?.paddingBottom ?? 0,
  );
  const parentRightPadding = parseFloat(
    getComputedStyle(scrollableParent)?.paddingRight ?? 0,
  );

  const parentTop = parentRect.top;
  const parentBottom = parentRect.bottom;
  const parentLeft = parentRect.left;
  const parentRight = parentRect.right;

  return (
    elementRect.top >= parentTop &&
    elementRect.bottom <= parentBottom - parentBottomPadding &&
    elementRect.left >= parentLeft &&
    elementRect.right <= parentRight - parentRightPadding
  );
};

export const scrollIntoView = ({
  element,
  block = 'nearest',
  inline = 'nearest',
  behavior = 'instant',
  gutter = 2,
}: {
  element: HTMLElement | null | undefined;
  block?: ScrollLogicalPosition;
  inline?: ScrollLogicalPosition;
  behavior?: ScrollBehavior;
  gutter?: number;
}) => {
  if (!element) {
    return;
  }

  const scrollableParent = getScrollableParent(element);

  if (!isElementVisible(element, scrollableParent)) {
    const beforeScroll = scrollableParent?.scrollTop ?? 0;
    element.scrollIntoView({ block, inline, behavior });
    const afterScroll = scrollableParent?.scrollTop ?? 0;

    if (afterScroll > beforeScroll) {
      // nudge the scroll position down by 2px so the focused option has some space above it
      scrollableParent?.scrollTo({
        top: afterScroll + gutter,
        behavior,
      });
    } else if (afterScroll < beforeScroll) {
      // nudge the scroll position up by 2px so the focused option has some space below it
      scrollableParent?.scrollTo({
        top: afterScroll - gutter,
        behavior,
      });
    }
  }
};
